import Button from '@oberoninternal/travelbase-ds/components/action/Button';
import Body from '@oberoninternal/travelbase-ds/components/primitive/Body';
import Title from '@oberoninternal/travelbase-ds/components/primitive/Title';
import useSesame from '@oberoninternal/travelbase-ds/hooks/useSesame';
import { gql } from '@apollo/client';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import React, { FC, useMemo } from 'react';
import { FormattedList, FormattedMessage, useIntl } from 'react-intl';
import { getCompanySeoMessages } from '../../constants/seoMessages';
import { addApolloState, initApolloClient } from '../../createApolloClient';
import { Locale } from '../../entities/Locale';
import NotFoundError from '../../entities/NotFoundError';
import {
    CompanyDetailDocument,
    CompanyDetailQuery,
    CompanyDetailQueryVariables,
    GenericImage,
    useCompanyDetailQuery,
} from '../../generated/graphql';
import createImgProps from '../../utils/createImgProps';
import TwoColumnIntro, { PracticalList, SocialsList } from '../TwoColumnIntro';
import CompanyOperatingHours from '../CompanyOperatingHours';
import ContentWrapper from '../ContentWrapper';
import Cell from '../designsystem/Cell';
import GradientBackground from '../designsystem/GradientBackground';
import Grid from '../designsystem/Grid';
import Heading from '../designsystem/Heading';
import Hero, { HeroButtonsDesktop, HeroButtonsMobile, HeroContainer, HeroPhotosButton } from '../Hero';
import Section from '../designsystem/Section';
import TextClamp from '../designsystem/TextClamp';
import { SectionDivider } from '../Divider';
import Intro from '../Intro';
import LoadingDetail from '../LoadingDetail';
import LocationMap, { devMarkerUrl, devMarkerUrl2x } from '../LocationMap';
import Page from '../Page';
import PhotoSection from '../PhotoSection';
import Email from '../svg/Email';
import Link from '../svg/Link';
import Phone from '../svg/Phone';
import Facebook from '../svg/socials/Facebook';
import Instagram from '../svg/socials/Instagram';
import Twitter from '../svg/socials/Twitter';
import Img from '../Img';
import styled from 'styled-components';
import MediaButton from '../designsystem/MediaButton';
import VideoModal from '../VideoModal';
import setCanonicalLink from '../../utils/setCanonicalLink';
import TranslatedPhoneNumber, { formatPhoneNumber } from '../TranslatedPhoneNumber';
import Play from '@oberoninternal/travelbase-ds/components/figure/Play';
import Head from 'next/head';
import { useTenantContext } from '../../context/TenantContext';
import FavoriteButton from '../FavoriteButton';
import { Box } from '@rebass/grid';
import Carousel, { CarouselChild } from '../designsystem/Carousel';
import ActivityHit from '../ActivityHit';
import { useTileImageSizes } from '../designsystem/Tile';
import parseToDateString from '../../constants/parseToDateString';
import addDays from 'date-fns/addDays';

export const pageQuery = gql`
    query CompanyDetail($slug: String!, $startDate: String!, $endDate: String!) {
        company(slug: $slug) {
            id
            videoUrl
            # intro
            jpegHero: mainImage {
                transform(config: HERO, format: JPEG) {
                    ...Transform
                }
            }
            webpHero: mainImage {
                transform(config: HERO, format: WEBP) {
                    ...Transform
                }
            }
            jpegThumbnail: listImage {
                transform(config: TILE, format: JPEG) {
                    ...Transform
                }
            }
            webpThumbnail: listImage {
                transform(config: TILE, format: WEBP) {
                    ...Transform
                }
            }

            place
            name
            description
            categories {
                id
                isRoot
                name
                position
                slug
            }

            # contact section
            socialsFacebookUrl
            socialsInstagramUrl
            socialsTwitterUrl
            email
            phoneNumber
            websiteUrl

            # location section
            address {
                ...Address
            }

            pngLogo: logoImage {
                transform(config: LOGO, format: PNG) {
                    ...Transform
                }
            }
            webpLogo: logoImage {
                transform(config: LOGO, format: WEBP) {
                    ...Transform
                }
            }
            coordinates {
                lat
                lon
            }

            ...CompanyOperatingHours

            # particulars section
            descriptionParticulars

            # images section
            images {
                jpegTile: transform(config: TILE, format: JPEG) {
                    ...Transform
                }
                webpTile: transform(config: TILE, format: WEBP) {
                    ...Transform
                }
                jpegGallery: transform(config: GALLERY, format: JPEG) {
                    ...Transform
                }
                webpGallery: transform(config: GALLERY, format: WEBP) {
                    ...Transform
                }
            }

            activities {
                ...ActivityHit
            }
        }
    }
`;
const CompanyDetail: FC<React.PropsWithChildren<unknown>> = () => {
    const router = useRouter();
    const { onOpen, onClose, open } = useSesame();
    const { open: videoModalIsOpen, onOpen: onOpenVideoModal, onClose: onCloseVideoModal } = useSesame();
    const intl = useIntl();
    const { brandConfig } = useTenantContext();
    const today = useMemo(() => new Date(), []);
    const { data, loading } = useCompanyDetailQuery({
        variables: {
            slug: router.query.slug as string,
            startDate: parseToDateString(today),
            endDate: parseToDateString(addDays(today, 7)),
        },
    });
    const sizes = useTileImageSizes();

    if (loading) {
        return <LoadingDetail withHero />;
    }

    if (!data?.company) {
        throw new NotFoundError();
    }

    const {
        name,
        jpegHero,
        webpHero,
        jpegThumbnail,
        webpThumbnail,
        categories,
        email,
        phoneNumber,
        websiteUrl,
        socialsFacebookUrl,
        socialsTwitterUrl,
        socialsInstagramUrl,
        address,
        coordinates,
        descriptionParticulars,
        images,
        normalOperatingHours,
        specialOperatingHours,
        pngLogo,
        webpLogo,
        description,
        videoUrl,
        id,
        activities,
    } = data.company;

    const directionsUrl = `https://www.google.com/maps/dir/${[
        address.street,
        address.number,
        address.postalCode,
        address.city,
    ]
        .filter(Boolean)
        .join(',')}`;

    const categoryNames = [...categories].sort((a, b) => a.position - b.position).map(category => category.name);

    const renderHero = (webp: GenericImage, jpeg: GenericImage, className?: string) => (
        <Hero ratio={9 / 16} {...createImgProps(jpeg, webp)} className={className}>
            {images.length > 0 && (
                <>
                    <HeroButtonsMobile>
                        <HeroPhotosButton onClick={onOpen}>{images.length}</HeroPhotosButton>
                        {videoUrl && (
                            <MediaButton icon={<Play />} onClick={() => onOpenVideoModal()}>
                                <FormattedMessage defaultMessage="Video" />
                            </MediaButton>
                        )}
                    </HeroButtonsMobile>
                    <HeroButtonsDesktop>
                        <HeroPhotosButton onClick={onOpen}>{images.length}</HeroPhotosButton>
                        {videoUrl && (
                            <MediaButton icon={<Play />} onClick={() => onOpenVideoModal()}>
                                <FormattedMessage defaultMessage="Video bekijken" />
                            </MediaButton>
                        )}
                    </HeroButtonsDesktop>
                </>
            )}
        </Hero>
    );
    const { title: seoTitle, description: seoDescription } = getCompanySeoMessages(data.company, intl);

    return (
        <>
            <Head>
                <title>{`${seoTitle} - ${brandConfig.seo.siteName}`}</title>
                {seoDescription && <meta name="description" content={seoDescription} />}
                <meta property="og:title" content={seoTitle} />
                {seoDescription && <meta name="og:description" content={seoDescription} />}
                {jpegThumbnail?.transform?.src && <meta property="og:image" content={jpegThumbnail.transform.src} />}
                {seoDescription && <meta property="og:description" content={seoDescription} />}
                <meta property="og:image:alt" content={seoTitle} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={seoTitle} />
                {seoDescription && <meta name="twitter:description" content={seoDescription} />}
                {jpegThumbnail?.transform?.src && <meta name="twitter:image" content={jpegThumbnail.transform.src} />}
                <meta name="twitter:image:alt" content={seoTitle} />
            </Head>

            <Page pb="8rem">
                {videoUrl && <VideoModal open={videoModalIsOpen} onClose={onCloseVideoModal} src={videoUrl} />}

                <HeroContainer>
                    {webpHero && jpegHero && renderHero(webpHero, jpegHero)}
                    {(!webpHero || !jpegHero) &&
                        webpThumbnail &&
                        jpegThumbnail &&
                        renderHero(webpThumbnail, jpegThumbnail, 'lt-s')}
                </HeroContainer>
                <Section name="intro">
                    <TwoColumnIntro
                        leftChildren={
                            <>
                                <Intro
                                    title={<FormattedList value={categoryNames} type="conjunction" />}
                                    description={description ?? undefined}
                                    tagline={name}
                                />
                                {brandConfig.enableFavorites && (
                                    <Box mb={[0, null, '64px']} mt={[8, null, 0]}>
                                        <FavoriteButton type="company" id={id} />
                                    </Box>
                                )}
                            </>
                        }
                        rightChildren={
                            <>
                                <Heading variant="small">
                                    <Title>
                                        <FormattedMessage defaultMessage="Contactgegevens" />
                                    </Title>
                                </Heading>
                                <PracticalList>
                                    {email && (
                                        <li>
                                            <Email /> <a href={`mailto:${email}`}>{email}</a>
                                        </li>
                                    )}
                                    {phoneNumber && (
                                        <li>
                                            <Phone />{' '}
                                            <a href={`tel:${formatPhoneNumber(phoneNumber, 'nl', false)}`}>
                                                <TranslatedPhoneNumber phoneNumber={phoneNumber} />
                                            </a>
                                        </li>
                                    )}
                                    {websiteUrl && (
                                        <li>
                                            <Link />{' '}
                                            <a href={websiteUrl} target="_blank" rel="noreferrer noopener">
                                                {websiteUrl}
                                            </a>
                                        </li>
                                    )}
                                </PracticalList>
                                <SocialsList>
                                    {socialsFacebookUrl && (
                                        <li>
                                            <a href={socialsFacebookUrl} target="_blank" rel="noreferrer noopener">
                                                <Facebook />
                                            </a>
                                        </li>
                                    )}
                                    {socialsInstagramUrl && (
                                        <li>
                                            <a href={socialsInstagramUrl} target="_blank" rel="noreferrer noopener">
                                                <Instagram />
                                            </a>
                                        </li>
                                    )}
                                    {socialsTwitterUrl && (
                                        <li>
                                            <a href={socialsTwitterUrl} target="_blank" rel="noreferrer noopener">
                                                <Twitter />
                                            </a>
                                        </li>
                                    )}
                                </SocialsList>
                            </>
                        }
                    />
                </Section>
                <Section id="address">
                    <GradientBackground />
                    <ContentWrapper>
                        <Grid>
                            <Cell gridColumn={['1 / -1', null, null, '1 / 5', null, '1 / 6']}>
                                {pngLogo && webpLogo && (
                                    <LogoContainer>
                                        <Img
                                            layout="fill"
                                            loading="lazy"
                                            {...createImgProps({ ...pngLogo, type: 'image/png' }, webpLogo)}
                                        />
                                    </LogoContainer>
                                )}
                                <Heading>
                                    <Title variant="large" elementType="h2">
                                        <FormattedMessage defaultMessage="Adres" />
                                    </Title>
                                </Heading>
                                {address.street && address.number && (
                                    <Body>
                                        {address.street} {address.number}
                                    </Body>
                                )}
                                <Body>
                                    {address.postalCode} {address.city}
                                </Body>
                                <Button
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    href={directionsUrl}
                                    variant="outline"
                                    style={{ marginTop: '1.6rem', cursor: 'pointer', display: 'inline-flex' }}
                                    as="a"
                                >
                                    <FormattedMessage defaultMessage="Routebeschrijving" />
                                </Button>
                                {normalOperatingHours && (
                                    <CompanyOperatingHours data={{ normalOperatingHours, specialOperatingHours }} />
                                )}
                            </Cell>
                            {coordinates && (
                                <Cell
                                    mt={[6, null, null, 0]}
                                    gridColumn={['1 / -1', null, null, '5 / -1', null, '6 / -1']}
                                >
                                    <LocationMap
                                        markerElement="flag"
                                        iconUrl={`${
                                            process.env.NEXT_PUBLIC_BASE_URL
                                                ? `${process.env.NEXT_PUBLIC_BASE_URL}/static/img/flagMarker.png`
                                                : devMarkerUrl
                                        }`}
                                        iconUrl2x={`${
                                            process.env.NEXT_PUBLIC_BASE_URL
                                                ? `${process.env.NEXT_PUBLIC_BASE_URL}/static/img/flagMarker2x.png`
                                                : devMarkerUrl2x
                                        }`}
                                        position={{
                                            longitude: coordinates.lon,
                                            latitude: coordinates.lat,
                                        }}
                                        markerPosition={{
                                            longitude: coordinates.lon,
                                            latitude: coordinates.lat,
                                        }}
                                    />
                                </Cell>
                            )}
                        </Grid>
                    </ContentWrapper>
                </Section>
                <SectionDivider />
                {descriptionParticulars && (
                    <Section id="particulars">
                        <ContentWrapper>
                            <Heading>
                                <Title variant="large">
                                    <FormattedMessage defaultMessage="Belangrijk om te weten" />
                                </Title>
                            </Heading>
                            <TextClamp style={{ maxWidth: '56.8rem' }} text={descriptionParticulars} linkifyUrls />
                        </ContentWrapper>
                    </Section>
                )}
                <SectionDivider mb="var(--wrapperPadding) !important" />
                <PhotoSection photos={images} open={open} onClose={onClose} onOpen={onOpen} />
                {activities?.length > 0 && (
                    <ContentWrapper>
                        <Carousel title={<FormattedMessage defaultMessage="Activiteiten" />} continuous>
                            {activities?.map(hit => (
                                <CarouselChild key={hit.name}>
                                    <ActivityHit sizes={sizes} hit={hit} cta={false} />
                                </CarouselChild>
                            ))}
                        </Carousel>
                    </ContentWrapper>
                )}
            </Page>
        </>
    );
};

export default CompanyDetail;

const LogoContainer = styled.div`
    position: relative;
    margin-bottom: 2.4rem;
    width: 10.4rem;
    height: 10.4rem;

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.m}) {
        margin-bottom: ${({ theme }) => theme.spacing['50_Semi']};
        width: 20rem;
        height: 20rem;
    }
`;

export const getServerSideProps: GetServerSideProps = async ctx => {
    const { query, req, res, locale } = ctx;
    setCanonicalLink(req, res);

    const client = initApolloClient(locale as Locale);

    const today = new Date();
    const { data } = await client.query<CompanyDetailQuery, CompanyDetailQueryVariables>({
        query: CompanyDetailDocument,
        variables: {
            slug: query.slug as string,
            startDate: parseToDateString(today),
            endDate: parseToDateString(addDays(today, 7)),
        },
    });

    if (!data?.company) {
        return {
            notFound: true,
        };
    }

    return addApolloState(client, { props: {} });
};
